import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import './index.scss';
import {setBellStatusAction, setIntercomStatusAction} from '../../store/control/actions';
import iconCallGreen from '../../assets/images/icons/call-screen/icon-call-green.png';
import iconCallEndRed from '../../assets/images/icons/call-screen/call-end-red.png';
import {useRPC} from '../../hooks/rpc';
import yellowDoorIcon from '../../assets/images/icons/intercom/door_open1_icon.png';
import blueDoorIcon from '../../assets/images/icons/intercom/door_open2_icon.png';
import pingDoorIcon from '../../assets/images/icons/intercom/door_open3_icon.png';
import limeDoorIcon from '../../assets/images/icons/intercom/door_open4_icon.png';
import {Timer} from '../../components/Timer';
import {useDispatch, useSelector} from 'react-redux';
import {IntercomModel} from '../../types/intercom';
import {onClickHandler} from '../../utils/onclick';
import {useOrientation} from '../../hooks/orientation';
import {useCurrentGateway} from '../../hooks/current-gateway';
import {CameraRenderer} from '../../components/CameraRenderer';
import {WindowUtil} from '../../utils/window';
import {useBackground} from '../../hooks/background-class';
import {useLocalization} from '../../hooks/localization';

const doors = [yellowDoorIcon, blueDoorIcon, pingDoorIcon, limeDoorIcon];
const colors = ['yellow', 'blue', 'pink', 'lime'];

export const CallScreen = memo(function CallScreen() {
    const [, , backgroundStyle] = useBackground();
    const {t} = useLocalization();
    const videoElement = useRef<HTMLVideoElement>();
    const dispatch = useDispatch();
    const gateway = useCurrentGateway();
    const orientation = useOrientation();
    const [rpc] = useRPC();
    const intercomState = useSelector(state => state.control.intercomState);
    const bellStatus = useSelector(state => state.control.bellStatus);
    const doorList = useSelector(state => state.control.doorList);
    const intercomSoundLevelDB = useSelector(state => state.control.sound.intercom_level);
    const incomingCallSoundLevelDB = useSelector(state => state.control.sound.incoming_call_outside_panel_level);
    const hasUSBCamera = gateway?.settings.intercom.has_usb_camera;

    const {status, info, is_incoming_call, name: intercomName} = intercomState;
    const [cameraId, setCameraId] = useState<string>(null);
    const [noImage, setNoImage] = useState(false);
    const [name, setName] = useState('');
    const [mediaPath, setMediaPath] = useState<MediaStream>(null);
    const [isCallOpen, setIsCallOpen] = useState(false);
    const [isCallStarted, setIsCallStarted] = useState(false);
    const [isIndoorCall, setIsIndoorCall] = useState(false);
    const [intercomSoundLevel, setIntercomSoundLevel] = useState(0);
    const [disabledLookup, setDisabledLookup] = useState({});

    const answerCall = useCallback((responseMessage: string) => {
        rpc.answerIntercomRequest(responseMessage);
    }, [rpc]);

    const openDoor = useCallback((door_id: string) => {
        if (disabledLookup[door_id]) {
            return;
        }

        rpc.openDoor(door_id);

        setDisabledLookup(lookup => ({
            ...lookup,
            [door_id]: true,
        }));

        setTimeout(() => {
            setDisabledLookup(lookup => ({
                ...lookup,
                [door_id]: false,
            }));
        }, 3000);
    }, [disabledLookup, rpc]);

    const hangUp = useCallback(() => {
        if (isIndoorCall) {
            dispatch(setBellStatusAction(null));
        } else {
            if (status === 'special') {
                dispatch(setIntercomStatusAction('ended', true));
            } else {
                answerCall('hang-up');
            }
        }
    }, [answerCall, dispatch, isIndoorCall, status]);

    const setInfo = useCallback((info: IntercomModel) => {
        let name = intercomName;
        let cameraId: string = null;

        if (info) {
            if (info.camera_id) {
                cameraId = info.camera_id;
            }

            if (!name) {
                if (info.name) {
                    name = info.name;
                } else if (info.sip_id) {
                    name = info.sip_id + ' ' + t('NUMBER_NO');
                }
            }
        }

        if (cameraId) {
            setCameraId(cameraId);
            setNoImage(false);
        } else {
            setNoImage(true);
        }

        setName(name ?? t('UNKNOWN_NUMBER'));
    }, [t, intercomName]);

    const changeSound = useCallback((newLevel: number) => {
        if (newLevel < 0) {
            changeSound(0);
        } else if (newLevel > 100) {
            changeSound(100);
        } else {
            rpc.updateSoundLevel(newLevel)
                .then(() => {
                    setIntercomSoundLevel(newLevel);
                });
        }
    }, [rpc]);

    const increaseSound = useCallback(() => {
        changeSound(intercomSoundLevel + 10);
    }, [changeSound, intercomSoundLevel]);

    const decreaseSound = useCallback(() => {
        changeSound(intercomSoundLevel - 10);
    }, [changeSound, intercomSoundLevel]);

    useEffect(() => {
        if (isCallStarted) {
            setIntercomSoundLevel(intercomSoundLevelDB);
        } else {
            setIntercomSoundLevel(incomingCallSoundLevelDB);
        }
    }, [isCallStarted, intercomSoundLevelDB, incomingCallSoundLevelDB]);

    useEffect(() => {
        if (isCallOpen && videoElement.current) {
            videoElement.current.srcObject = mediaPath;
        }

        return () => {
            if (mediaPath) {
                mediaPath.getTracks()[0].stop();
            }
        };
        // eslint-disable-next-line
    }, [mediaPath, isCallOpen]);

    useEffect(
        () => {
            switch (status) {
                case 'special':
                    setInfo(info);
                    setIsCallOpen(true);
                    setIsCallStarted(true);
                    break;
                case 'incoming':
                    setInfo(info);
                    setIsCallOpen(true);
                    setIsCallStarted(false);
                    break;
                case 'timeout':
                case 'rejected':
                case 'ended':
                case 'failed':
                case 'cancelled':
                    if (!isIndoorCall) {
                        setIsCallOpen(false);
                        setIsCallStarted(false);
                    }
                    break;
                case 'in_call':
                    setIsCallStarted(true);
                    break;
            }
        },
        [status, isCallStarted, isCallOpen, isIndoorCall, cameraId, info, setInfo],
    );

    useEffect(
        () => {
            if (bellStatus === 'incoming') {
                if (isCallOpen) {
                    return;
                }

                if (hasUSBCamera) {
                    setName(t('DOORBELL'));
                    navigator.mediaDevices.getUserMedia({video: true})
                        .then(stream => {
                            if (mediaPath !== stream) {
                                setMediaPath(stream);
                                setNoImage(false);
                            }
                        })
                        .finally(() => {
                            setIsCallOpen(true);
                            setIsCallStarted(true);
                            setIsIndoorCall(true);
                        });
                } else {
                    setName(t('DOORBELL'));
                    setNoImage(true);
                    setIsCallOpen(true);
                    setIsCallStarted(true);
                    setIsIndoorCall(true);
                }
            } else {
                if (isIndoorCall) {
                    setIsIndoorCall(false);
                    setIsCallStarted(false);
                    setIsCallOpen(false);
                }
            }
        },
        // eslint-disable-next-line
        [bellStatus, isCallOpen, isIndoorCall, mediaPath],
    );

    useEffect(() => {
        let timeout;
        if (isCallOpen) {
            timeout = setTimeout(() => {
                hangUp();
            }, 5 * 60 * 1000);
        }

        return () => {
            clearTimeout(timeout);
        };

        // eslint-disable-next-line
    }, [isCallOpen]);

    //todo fix
    useEffect(() => {
        if (isCallOpen) {
            //WindowUtil.cancel();
            console.log("245")
        } else {
            //WindowUtil.stop();
            console.log("249")
        }
    }, [isCallOpen]);

    if (!isCallOpen) {
        return null;
    }

    return (
        <div className={'call-screen-wrapper ' + orientation} style={backgroundStyle}>
            {
                renderIf(!isIndoorCall && !noImage)(() =>
                        <CameraRenderer className="preview" mode="auto" id={cameraId} quality={720}/>,
                    () =>
                        renderIf(!noImage)(()=>(
                            <video ref={videoElement} autoPlay={true}/>
                        ))
                )
            }
            {
                renderIf(name)(() =>
                    <div className="call-header">
                        {
                            renderIf(!isCallStarted && !isIndoorCall)(() =>
                                <img src={iconCallGreen} alt=""/>,
                            )
                        }
                        <span className="name">{name}</span>
                        {
                            renderIf(!isCallStarted && !isIndoorCall)(() =>
                                    <span>{is_incoming_call ? t('CALLING') : t('OUT_CALLING')}</span>,
                                () => <Timer/>,
                            )
                        }
                    </div>,
                )
            }
            <div className="call-control-right">
                {
                    renderIf(!isIndoorCall && info != null && info.door_id_list.length > 0)(() =>
                        info.door_id_list.map((door_id, index) => {
                            const door = doorList.find(door => door.id === door_id);
                            if (!door) {
                                return null;
                            }

                            return (
                                <button
                                    key={`door_${door.id}`}
                                    className={'button button-transparent lg ' + colors[index % colors.length] + (disabledLookup[door.id] ? ' disabled' : '')}
                                    {...onClickHandler(() => openDoor(door.id))}>
                                    <img src={doors[index % doors.length]} alt=""/>
                                    <span>{door.name}</span>
                                </button>
                            );
                        }),
                    )
                }
            </div>
            <div className="call-control-bottom">
                {
                    renderIf(!isCallStarted && !isIndoorCall && is_incoming_call)(() =>
                        <button className="button button-transparent color-green lg"
                                {...onClickHandler(() => answerCall('answer'))}>
                            <img src={iconCallGreen} alt=""/>
                            <span>{t('ANSWER_CALL')}</span>
                        </button>,
                    )
                }
                <button className="button button-transparent color-red lg" {...onClickHandler(hangUp)}>
                    <img src={iconCallEndRed} alt=""/>
                    <span>
                        {intercomState.status === 'special' ? t('END_WATCHING') : ((intercomState.status === 'incoming' && (!isCallStarted && !isIndoorCall)) ? t('REJECT_CALL') : t('END_CALL'))}
                    </span>
                </button>
            </div>
            {
                renderIf(!isIndoorCall && intercomState.status !== 'special')(() =>
                    <div className="call-control-left">
                        <div className="button-container">
                            <span className="button" {...onClickHandler(increaseSound)}>+</span>
                            <span className="value">{intercomSoundLevel}</span>
                            <span className="button" {...onClickHandler(decreaseSound)}>-</span>
                        </div>
                    </div>,
                )
            }
        </div>
    );
});
